/**
 * React Query hooks for authentication.
 *
 * Provides both a composite useAuth() hook (bundles status + mutations + toasts)
 * and individual hooks (useLogin, useRegister, useForgotPassword, useLogout)
 * for fine-grained control without toasts.
 *
 * JWT persists in localStorage under 'gdb_token'. On login/register success,
 * the token is stored and auth status is set in the React Query cache.
 * On logout, the token is removed and all auth queries are invalidated.
 */

import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  login,
  register,
  forgotPassword,
  resetPassword,
  refreshAuth,
  getAuthStatus,
  getMe,
  logout,
} from './authApi'
import { authKeys } from './authKeys'
import { setAuthToken, TOKEN_KEY } from './setAuthToken'
import { DEFAULT_ENVIRONMENT } from './types'
import type {
  AuthResponse,
  AuthStatus,
  ForgotPasswordResult,
  ResetPasswordResult,
} from './types'

// ---------------------------------------------------------------------------
// Composite hook -- bundles auth status + mutations with toast notifications
// ---------------------------------------------------------------------------

/**
 * Composite auth hook. Preferred for most UI usage.
 *
 * - Queries auth status on mount via getMe() if a stored token exists.
 * - Provides login/logout/refresh mutations with React Query cache management.
 * - Shows toast notifications on login/logout success and on errors.
 */
// Bundled-local (in-plugin) build: the SPA is served BY the plugin's local
// sidecar (gdb.ini SpaSource=bundled) and must run FULLY OFFLINE — no cloud
// origin is ever contacted (Craig 2026-07-31: the plugin "does not ever call
// app.gamedevbuddy.com"). Auth is bypassed entirely: the user is treated as
// logged in and CC-entitled, and the auth-status network query never runs.
// Updates/purchases happen on the live WEBSITE, not in the editor.
// Set at build time by the WebApp staging build (VITE_GDB_BUNDLED=1) — the
// public website build never sets it, so the site keeps real auth.
export const GDB_BUNDLED_LOCAL = import.meta.env.VITE_GDB_BUNDLED === '1'

const BUNDLED_LOCAL_STATUS: AuthStatus = {
  loggedIn: true,
  valid: true,
  user: {
    id: 'local',
    email: 'local@plugin',
    name: 'Character Creator',
    tier: 'standard',
    role: 'user',
    roles: [],
  },
  modules: [
    {
      id: 'character-creator',
      name: 'Character Creator',
      hasAccess: true,
      canUpdate: false,
      hasSourceAccess: false,
    },
  ],
  maintenanceActive: false,
  needsRefresh: false,
  offlineGraceDays: 365,
  error: null,
}

export function useAuth() {
  const queryClient = useQueryClient()

  // Cross-surface auth sync. The plugin renders independent WebViews (the main
  // panel plus any detached Account/Store companion tabs) that share ONE
  // localStorage. When any panel logs in/out, setAuthToken writes 'gdb_token',
  // and the `storage` event fires in every OTHER same-origin panel. Re-validate
  // auth on that signal so all surfaces update instantly — bridge-INDEPENDENT,
  // so it works even while the UE bridge is down. (The acting panel updates via
  // its own mutation; the `storage` event only notifies the others.
  // e.key === null = localStorage cleared.)
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === TOKEN_KEY || e.key === null) {
        queryClient.invalidateQueries({ queryKey: authKeys.status() })
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [queryClient])

  // Auth status query — asks the CLOUD directly via /api/auth/status
  // (authClient → authUrl). Since the auth proxy was removed, the cloud is
  // the auth authority; the sidecar no longer holds a proxied session.
  // localStorage['gdb_token'] is the Bearer cache that authClient attaches;
  // getAuthStatus() degrades any non-200 (e.g. the cloud's 401 when no Bearer
  // is present) into { success:false }, which we render as logged-out.
  const {
    data: authStatus,
    isLoading,
  } = useQuery({
    queryKey: authKeys.status(),
    queryFn: async (): Promise<AuthStatus> => {
      // Bundled-local: static logged-in+entitled status, ZERO network.
      if (GDB_BUNDLED_LOCAL) {
        return BUNDLED_LOCAL_STATUS
      }
      const response = await getAuthStatus()
      if (!response.success || !response.data) {
        // Cloud unreachable or no valid session — return a logged-out shape
        // so the UI shows the AuthCard rather than a loading spinner.
        return {
          loggedIn: false,
          valid: false,
          user: null,
          modules: [],
          maintenanceActive: false,
          needsRefresh: false,
          offlineGraceDays: 7,
          error: response.error || null,
        }
      }

      // The cloud may return a rotated token on /status. Route it through
      // setAuthToken so BOTH the localStorage Bearer cache AND the sidecar's
      // held token stay in sync — the sidecar needs the latest JWT for its
      // own authed cloud calls (module downloads, licence checks).
      if (response.data.token) {
        setAuthToken(response.data.token, true)
      } else if (!response.data.loggedIn) {
        // No valid session — clear the local Bearer cache so we don't keep
        // sending a stale token to module endpoints.
        localStorage.removeItem(TOKEN_KEY)
      }

      return response.data
    },
    // Refetch every 30 s and on window focus so React's view of auth +
    // entitlements matches the cloud within a reasonable window without
    // spamming (this is how a fresh purchase grant shows up without a reload).
    // The query is the canonical truth check; mutations
    // (login/logout) invalidate it explicitly so login transitions are
    // instant rather than waiting for the next stale-check.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: false,
  })

  // Login mutation with toast
  const loginMutation = useMutation({
    mutationFn: ({
      email,
      password,
      rememberMe,
    }: {
      email: string
      password: string
      rememberMe?: boolean
    }) => login(email, password, rememberMe ?? true),
    onSuccess: (
      response: AuthResponse<AuthStatus>,
      variables: { email: string; password: string; rememberMe?: boolean },
    ) => {
      if (response.success && response.data) {
        // Store JWT + hand it off to the sidecar. rememberMe mirrors the
        // login form so the sidecar persists (or not) the same way.
        if (response.data.token) {
          setAuthToken(response.data.token, variables.rememberMe ?? true)
        }
        // Populate React Query cache
        queryClient.setQueryData(authKeys.status(), response.data)
        toast.success('Logged in successfully')
      } else {
        toast.error(response.error || 'Login failed')
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Login failed'
      toast.error(message)
    },
  })

  // Logout mutation with toast
  const logoutMutation = useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all })
      toast.success('Logged out')
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Logout failed'
      toast.error(message)
    },
  })

  // Refresh mutation (background, no toast on success -- only error)
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem(TOKEN_KEY)
      if (!token) {
        return { success: false, error: 'No token stored' } as AuthResponse<AuthStatus>
      }
      return refreshAuth(token)
    },
    onSuccess: (response: AuthResponse<AuthStatus>) => {
      if (response.success && response.data) {
        // Update stored token if refreshed, and re-hand-off to the sidecar so
        // it holds the latest JWT too. A refresh implies an existing session,
        // so rememberMe stays true.
        if (response.data.token) {
          setAuthToken(response.data.token, true)
        }
        queryClient.setQueryData(authKeys.status(), response.data)
      }
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Session refresh failed'
      toast.error(message)
    },
  })

  return {
    authStatus: authStatus ?? null,
    isLoading,
    isLoggedIn: authStatus?.loggedIn ?? false,
    user: authStatus?.user ?? null,
    modules: authStatus?.modules ?? [],
    // Backend-reported deployment environment. Defaults to the most
    // restrictive (`production`) until the status resolves, so pre-release
    // (alpha) modules never flash in before the real environment is known.
    environment: authStatus?.environment ?? DEFAULT_ENVIRONMENT,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
    refresh: refreshMutation.mutate,
    isRefreshing: refreshMutation.isPending,
  }
}

// ---------------------------------------------------------------------------
// Individual hooks -- fine-grained control without toasts
// ---------------------------------------------------------------------------

/**
 * Login with email and password.
 * On success: stores token in localStorage, sets auth status in query cache.
 * Caller handles UI feedback (no toast).
 */
export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      email,
      password,
      rememberMe,
    }: {
      email: string
      password: string
      rememberMe?: boolean
    }): Promise<AuthResponse<AuthStatus>> => {
      return login(email, password, rememberMe ?? true)
    },
    onSuccess: (
      response: AuthResponse<AuthStatus>,
      variables: { email: string; password: string; rememberMe?: boolean },
    ) => {
      if (response.success && response.data) {
        if (response.data.token) {
          setAuthToken(response.data.token, variables.rememberMe ?? true)
        }
        queryClient.setQueryData(authKeys.status(), response.data)
      }
    },
  })
}

/**
 * Register a new account. Auto-logs-in on success (stores token, sets query data).
 * Caller handles UI feedback (no toast).
 */
export function useRegister() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      name,
      email,
      password,
    }: {
      name: string
      email: string
      password: string
    }): Promise<AuthResponse<AuthStatus>> => {
      return register(name, email, password)
    },
    onSuccess: (response: AuthResponse<AuthStatus>) => {
      if (response.success && response.data) {
        // Register has no rememberMe control — default to true (stay signed in).
        if (response.data.token) {
          setAuthToken(response.data.token, true)
        }
        queryClient.setQueryData(authKeys.status(), response.data)
      }
    },
  })
}

/**
 * Request a password reset email.
 * Caller handles UI feedback (no toast).
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: async ({
      email,
    }: {
      email: string
    }): Promise<AuthResponse<ForgotPasswordResult>> => {
      return forgotPassword(email)
    },
  })
}

/**
 * Complete a password reset with a token + new password.
 *
 * Mirrors useForgotPassword: a plain mutation that returns the AuthResponse so
 * the caller (ResetPasswordForm) drives the UI feedback (success toast +
 * navigate, or error toast on failure). No token persistence here — a reset
 * does NOT log the user in; they go to /login afterwards.
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: async ({
      token,
      newPassword,
    }: {
      token: string
      newPassword: string
    }): Promise<AuthResponse<ResetPasswordResult>> => {
      return resetPassword(token, newPassword)
    },
  })
}

/**
 * Logout: clears token from localStorage, invalidates all auth queries.
 * Caller handles UI feedback (no toast).
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => logout(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.all })
    },
  })
}
